import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Plane, TrendingUp, TrendingDown, AlertTriangle, Fuel, Globe, DollarSign } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article4 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-cyan-600 text-white mr-4">
              <Plane className="h-3 w-3 mr-1" />
              Air Cargo
            </Badge>
            <span className="text-sm text-gray-600">Upply Market Insights / IATA / WorldACD</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>7 min read</span>
          </div>
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.4.title')}
          </h1>

          <img
            src="https://market-insights.upply.com/hs-fs/hubfs/market-insights/aerien/palette-avion_tarmac_iStock-2200360493.jpeg?width=730&height=395&name=palette-avion_tarmac_iStock-2200360493.jpeg"
            alt="Air cargo pallets on tarmac"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.4.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-cyan-50 p-6 rounded-lg my-8 border border-cyan-200">
              <h3 className="font-bold text-cyan-900 mb-4">
                {language === 'ko' ? '핵심 수치 (2026년 2~3월)' : 'Key Figures (February–March 2026)'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-1 flex items-center text-cyan-800">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '2월 물량 (YoY)' : 'Feb Volumes (YoY)'}
                  </h4>
                  <p className="text-2xl font-bold text-green-600">+11.2%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '국제선 +11.6%' : 'International +11.6%'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center text-cyan-800">
                    <TrendingDown className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '중동 용량 (YoY)' : 'ME Capacity (YoY)'}
                  </h4>
                  <p className="text-2xl font-bold text-red-600">-33%</p>
                  <p className="text-xs text-gray-500">{language === 'ko' ? '3월 하반기 기준' : 'Second half of March'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center text-cyan-800">
                    <DollarSign className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '두바이→미국 현물운임 (YoY)' : 'Dubai→US Spot Rate (YoY)'}
                  </h4>
                  <p className="text-2xl font-bold text-red-600">+152%</p>
                  <p className="text-xs text-gray-500">$10.33/kg</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.4.section1.title')}
            </h2>
            <p className="mb-6">{t('article.4.p1')}</p>
            <p className="mb-6">{t('article.4.p2')}</p>

            {/* February Corridor Data */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                {language === 'ko' ? '2026년 2월 주요 노선별 물량 증감 (YoY)' : 'February 2026 Volume Growth by Corridor (YoY)'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '노선' : 'Corridor'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '증감률' : 'Growth'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '비고' : 'Note'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '유럽–아시아' : 'Europe–Asia'}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">+13.1%</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{language === 'ko' ? '2자리수 성장 8개월 연속, 17개월 최고' : '8th consecutive month of double-digit growth, 17-month high'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '중동–아시아' : 'Middle East–Asia'}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">+24%</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{language === 'ko' ? '2자리수 성장 2개월 연속 — 분쟁 전' : 'Double-digit for 2nd consecutive month — pre-conflict'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '아시아–북미' : 'Asia–North America'}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">+9.1%</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{language === 'ko' ? '1월 소폭 감소 이후 반등' : 'Rebound after slight January contraction'}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '유럽–북미' : 'Europe–North America'}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">+5.7%</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{language === 'ko' ? '25개월 연속 플러스 성장' : '25-month positive streak'}</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '유럽–중동' : 'Europe–Middle East'}</td>
                      <td className="py-2 px-3 text-green-600 font-bold">+9.3%</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{language === 'ko' ? '전월과 동일한 수치' : 'Unchanged from previous month'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ko' ? '출처: IATA (CTK: 화물 톤-킬로미터)' : 'Source: IATA (CTK: Cargo Tonne-Kilometres)'}
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.4.section2.title')}
            </h2>
            <p className="mb-6">{t('article.4.p3')}</p>
            <p className="mb-6">{t('article.4.p4')}</p>

            {/* Conflict Impact Timeline */}
            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-200">
              <h3 className="font-bold text-red-900 mb-4 flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5" />
                {language === 'ko' ? '분쟁 발발 후 중동 항공화물 용량 변화' : 'Middle East Air Cargo Capacity After Conflict Outbreak'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-center">
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '분쟁 1주차' : 'Week 1 of conflict'}</p>
                  <p className="text-xl font-bold text-red-600">⬇ Plunge</p>
                  <p className="text-xs">{language === 'ko' ? '주요 공항 폐쇄, 지역 항공사 운항 중단' : 'Major airports closed, regional airlines grounded'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '3월 하반기 (직전 2주 대비)' : 'Mar second half (vs first half)'}</p>
                  <p className="text-xl font-bold text-orange-600">+31%</p>
                  <p className="text-xs">{language === 'ko' ? '일부 회복세' : 'Partial recovery'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '3월 하반기 (전년 대비)' : 'Mar second half (YoY)'}</p>
                  <p className="text-xl font-bold text-red-700">-33%</p>
                  <p className="text-xs">{language === 'ko' ? '여전히 전년 대비 대폭 감소' : 'Still significantly below year-on-year'}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                {language === 'ko' ? '출처: WorldACD — 다른 모든 지역은 주간 및 전년 대비 모두 용량 증가 기록' : 'Source: WorldACD — All other regions registered capacity growth on a WoW and YoY basis'}
              </p>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.4.section3.title')}
            </h2>
            <p className="mb-6">{t('article.4.p5')}</p>
            <p className="mb-6">{t('article.4.p6')}</p>

            {/* Rate Table */}
            <div className="bg-orange-50 p-6 rounded-lg my-8 border border-orange-200">
              <h3 className="font-bold text-orange-900 mb-4 flex items-center">
                <Fuel className="mr-2 h-5 w-5" />
                {language === 'ko' ? '현물 운임 급등 현황 (3월 말 기준)' : 'Spot Rate Surge — End of March 2026'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-orange-100">
                      <th className="text-left py-2 px-3">{language === 'ko' ? '노선' : 'Route'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '현물 운임' : 'Spot Rate'}</th>
                      <th className="text-left py-2 px-3">{language === 'ko' ? '주간 변동' : 'WoW'}</th>
                      <th className="text-left py-2 px-3">YoY</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '두바이→미국' : 'Dubai → USA'}</td>
                      <td className="py-2 px-3 font-bold">$10.33/kg</td>
                      <td className="py-2 px-3 text-red-600 font-bold">+28%</td>
                      <td className="py-2 px-3 text-red-600 font-bold">+152%</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '두바이→유럽' : 'Dubai → Europe'}</td>
                      <td className="py-2 px-3 font-bold">$5.44/kg</td>
                      <td className="py-2 px-3 text-red-600">+28%</td>
                      <td className="py-2 px-3 text-red-600 font-bold">+84% (×3)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '인도→미국' : 'India → USA'}</td>
                      <td className="py-2 px-3 font-bold">$7.77/kg</td>
                      <td className="py-2 px-3 text-red-600">↑</td>
                      <td className="py-2 px-3 text-red-600 font-bold">+73%</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '중동·남아시아→유럽' : 'ME/S.Asia → Europe'}</td>
                      <td className="py-2 px-3">—</td>
                      <td className="py-2 px-3 text-red-600">+5%</td>
                      <td className="py-2 px-3 text-red-600">+84%</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3 font-semibold">{language === 'ko' ? '아시아-태평양→유럽' : 'Asia-Pacific → Europe'}</td>
                      <td className="py-2 px-3">—</td>
                      <td className="py-2 px-3 text-orange-600">+4%</td>
                      <td className="py-2 px-3 text-orange-600">+28%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {language === 'ko' ? '출처: WorldACD / Upply (2026년 3월 23~29일 기준)' : 'Source: WorldACD / Upply (week of March 23–29, 2026)'}
              </p>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.4.section4.title')}
            </h2>
            <p className="mb-6">{t('article.4.p7')}</p>

            {/* Willie Walsh Quote */}
            <blockquote className="border-l-4 border-cyan-500 pl-6 py-2 my-6 bg-cyan-50 rounded-r-lg">
              <p className="text-gray-700 italic text-sm leading-relaxed">
                {language === 'ko'
                  ? '"가파른 연료 비용 상승, 일부 지역의 연료 부족, 걸프만 주요 화물 허브의 심각한 혼란은 대규모 변화입니다."'
                  : '"Sharply rising fuel costs, fuel scarcity in parts of the world, and the severe disruption to key cargo hubs in the Gulf are major shifts."'}
              </p>
              <footer className="text-xs text-gray-500 mt-2">— Willie Walsh, {language === 'ko' ? 'IATA 사무총장' : 'IATA Managing Director'}</footer>
            </blockquote>

            <p className="mb-6">{t('article.4.p8')}</p>

            {/* IMF macro table */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? '2026년 2월 거시경제 선행지표 (분쟁 발발 전)' : 'February 2026 Macro Leading Indicators (Pre-Conflict)'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-white rounded p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '글로벌 제조업 PMI' : 'Global Manufacturing PMI'}</p>
                  <p className="text-2xl font-bold text-blue-600">53.1</p>
                  <p className="text-xs text-green-600">{language === 'ko' ? '2021년 12월 이후 최고' : 'Highest since Dec 2021'}</p>
                </div>
                <div className="bg-white rounded p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">{language === 'ko' ? '수출 주문' : 'Export Orders'}</p>
                  <p className="text-2xl font-bold text-blue-600">51.4</p>
                  <p className="text-xs text-green-600">{language === 'ko' ? '2021년 7월 이후 최고' : 'Highest since Jul 2021'}</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3 text-center italic">
                {language === 'ko' ? '※ 분쟁 발발 이후 이 지표들의 신뢰성은 급격히 낮아졌습니다.' : '※ These indicators lost credibility rapidly after the conflict outbreak.'}
              </p>
            </div>

            <p className="mb-6">{t('article.4.conclusion')}</p>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2 text-green-600">✓</span><span>{language === 'ko' ? '2026년 2월 글로벌 항공화물 물량 YoY +11.2%, 국제선 적재율 51.5%로 강한 반등' : 'Global air cargo volumes +11.2% YoY in February 2026, international load factor at 51.5%'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-green-600">✓</span><span>{language === 'ko' ? '유럽-아시아 노선 2자리수 성장 8개월 연속(+13.1%), 중동-아시아 +24% 기록' : 'Europe-Asia corridor 8th consecutive double-digit growth (+13.1%), Middle East-Asia +24%'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-600">✗</span><span>{language === 'ko' ? '2월 28일 중동 분쟁 발발 — 1주차 주요 공항 폐쇄·지역 항공사 운항 중단' : 'Middle East conflict erupted 28 February — major airports closed and regional airlines grounded in week 1'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-600">✗</span><span>{language === 'ko' ? '3월 중동·남아시아 용량 전년 대비 -33% 지속' : 'Middle East & South Asia capacity remained -33% YoY in March second half'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-red-600">✗</span><span>{language === 'ko' ? '등유 가격 사상 최고치 돌파 — 두바이발 미국행 현물운임 YoY +152%($10.33/kg)' : 'Kerosene hit record highs — Dubai-to-US spot rate surged +152% YoY to $10.33/kg'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-orange-600">!</span><span>{language === 'ko' ? 'IMF 경고: "모든 길은 더 높은 물가와 더 느린 성장으로 이어진다"' : 'IMF warns: "All roads lead to higher prices and slower growth"'}</span></li>
                <li className="flex items-start"><span className="mr-2 text-orange-600">!</span><span>{language === 'ko' ? '운임 안정화 여부는 연료 가격 하락과 중동 분쟁 지속 기간에 달려 있음' : 'Rate stabilisation depends on fuel price decline and duration of the Middle East conflict'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Upply Market Insights — Air cargo: momentum torpedoed by the conflict in the Middle East.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://market-insights.upply.com/en/air-cargo-momentum-torpedoed-by-the-conflict-in-the-middle-east"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article4;
